import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styles from "@src/components/pages/admin-panel-page/admin-panel-page.module.scss";
import AdminPanelSidebar from "@src/components/molecules/admin-panel-sidebar/admin-panel-sidebar";
import {
  AdminPanelView,
  CrossNameSpaceOrganziation,
} from "@src/types/namespace-configuration";
import { MainContainer } from "@src/components/templates/layouts/main-container/main-container";
import OrganizationsViewAdminPanel from "@src/components/organisms/organizations-view-admin-panel/organizations-view-admin-panel";
import MachineGroupsViewAdminPanel from "@src/components/organisms/machine-groups-view-admin-panel/machine-groups-view-admin-panel";
import CleanupViewAdminPanel from "@src/components/organisms/cleanup-view-admin-panel/cleanup-view-admin-panel";
import { getOrganizationsUseCase } from "@src/cloud-connect/namespace-configuration/use-cases/get-organizations-use-case";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { useI18N } from "@src/language/language-context";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";
import { RootState } from "@src/reducers/store";

const cssClassName = "admin-panel-page";

const AdminPanelPage = () => {
  const i18n = useI18N();
  const showGatewayCleanup = useFeatureFlag("show-gateway-cleanup", false);
  const namespace = useSelector(
    (state: RootState) => state.yourSlice.authContext?.customerNamespace,
  );
  const [organizations, setOrganizations] = useState<
    CrossNameSpaceOrganziation[]
  >([]);
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(true);
  const [activeView, setActiveView] = useState<AdminPanelView>("organization");
  const texts = {
    failedToFetchOrganizations: i18n(
      "modules.adminPanelPage.failedToFetchOrganizations",
    ),
  };

  useEffect(() => {
    let cancelled = false;
    setOrganizations([]);
    setIsLoadingOrganizations(true);

    if (!namespace) {
      setIsLoadingOrganizations(false);
      return () => {
        cancelled = true;
      };
    }

    const loadOrganizations = async () => {
      try {
        const result = await getOrganizationsUseCase();
        if (cancelled) return;

        if (result.success) {
          setOrganizations(Array.isArray(result.data) ? result.data : []);
        } else {
          errorSnackbar(texts.failedToFetchOrganizations);
        }
      } catch (error) {
        if (!cancelled) {
          console.error(error);
          errorSnackbar(texts.failedToFetchOrganizations);
        }
      } finally {
        if (!cancelled) setIsLoadingOrganizations(false);
      }
    };

    void loadOrganizations();

    return () => {
      cancelled = true;
    };
  }, [namespace]);

  const renderCurrentView = () => {
    switch (activeView) {
      case "organization":
        return (
          <OrganizationsViewAdminPanel
            isLoadingOrganizations={isLoadingOrganizations}
            organizations={organizations}
          />
        );
      case "machine-groups":
        return (
          <MachineGroupsViewAdminPanel
            isLoadingOrganizations={isLoadingOrganizations}
            organizations={organizations}
          />
        );
      case "cleanup":
        return showGatewayCleanup ? <CleanupViewAdminPanel /> : null;
      default:
        return null;
    }
  };

  return (
    <div className={styles[cssClassName]}>
      <MainContainer>
        <div className={styles[`${cssClassName}__content`]}>
          <div className={styles[`${cssClassName}__inner-container`]}>
            <AdminPanelSidebar onViewChange={setActiveView} />
            <main className={styles[`${cssClassName}__view-content`]}>
              {renderCurrentView()}
            </main>
          </div>
        </div>
      </MainContainer>
    </div>
  );
};

export { AdminPanelPage };
